/**
 * Case 1 change affordance (Ashok ruling, 2026-08-04). The PASSIVE
 * counterpart to CategoryConfirmRequired: where that component COMPELS a
 * selection because the scorer found no vocabulary, this one offers an
 * optional change to a category the scorer was confident about. Two
 * different events - see category_decision_source's value set in
 * backend/routes/procurement.py (user_selected_low_signal vs user_changed).
 *
 * Reuses CategorySelectCell - the SAME picker the forced gate opens, and
 * the same one the Lines step uses. Not a second dropdown implementation
 * (identical rationale to CategoryConfirmRequired's own docstring: rank-
 * never-restrict, full master list, search and tiers).
 *
 * Deliberately NOT implemented by rendering CategoryConfirmRequired itself:
 * that component's own copy states "A category could not be determined from
 * your description", which is FALSE on this path - a category WAS
 * determined, with signal behind it, and the buyer is choosing to disagree.
 * Reusing it would have shown the buyer a misrepresentation of their own
 * document's state to save a file. The PICKER is shared; the framing that
 * explains WHY the picker is open is not, because the two reasons differ.
 *
 * CANCEL WRITES NOTHING. Closing this control fires no request at all - the
 * absence of a decision must never be recorded as one (DATAFLOW Principle
 * 16). Confirming the SAME category does fire, and the backend suppresses
 * it as a no-op; that asymmetry is deliberate and lives in one place
 * (the endpoint), not two.
 */
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import api from '@/lib/api';
import CategorySelectCell from '@/components/CategorySelectCell';

const CategoryChangeControl = ({
  userRequirement,
  currentCategoryCode,
  onChange,
  onCancel,
  // NO-FIT (F1 / C1b-1b), optional and absent by default - see
  // CategoryConfirmRequired's own note.
  noFitSubject = null,
  onDeclareNoFit = null,
}) => {
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let isMounted = true;
    api.getMasterCategories('selection')
      .then((response) => {
        if (isMounted) setCategories(response.data || []);
      })
      .catch(() => {
        if (isMounted) setCategories([]);
      })
      .finally(() => {
        if (isMounted) setLoadingCategories(false);
      });
    return () => { isMounted = false; };
  }, []);

  const handleChange = async (code) => {
    setSaving(true);
    try {
      await onChange(code);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-2" data-testid="category-change-control">
      <div className="flex items-center gap-2">
        {/* Seeded with the current code (unlike the forced gate, which must
            never pre-select its unconfirmed guess): here the value IS a
            confirmed-by-signal category, and showing it is what makes
            "change it to something else" a coherent action. */}
        <CategorySelectCell
          value={currentCategoryCode || ''}
          onChange={handleChange}
          description={userRequirement}
          index={0}
          lineItems={[]}
          categories={categories}
          loadingCategories={loadingCategories}
          assessedCategoryCode={null}
          noFitSubject={noFitSubject}
          onDeclareNoFit={onDeclareNoFit}
        />
        {saving && <Loader2 className="w-4 h-4 animate-spin text-zinc-500" data-testid="category-change-saving" />}
      </div>
      <button
        type="button"
        onClick={onCancel}
        className="mt-2 text-xs text-zinc-500 underline hover:text-zinc-700"
        data-testid="category-change-cancel"
      >
        Cancel
      </button>
    </div>
  );
};

export default CategoryChangeControl;
